import React, { useLayoutEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';

import Header from './components/Header';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';
import SeoManager from './components/SeoManager';

import HomePage from './pages/HomePage';
import BuyersPage from './pages/BuyersPage';
import LandlordsPage from './pages/LandlordsPage';
import SellersPage from './pages/SellersPage';
import TenantsPage from './pages/TenantsPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import ReportRepairPage from './pages/ReportRepairPage';
import ValuationPage from './pages/ValuationPage';
import NewsArticlePage from './pages/NewsArticlePage';
import PropertyPage from './pages/PropertyPage';
import PropertiesAdvancedPage from './pages/PropertiesAdvancedPage';
import PaymentsPage from './pages/PaymentsPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import AdminPage from './pages/AdminPage';

const theme = createTheme({
  palette: {
    primary: {
      main: '#19356f',
      dark: '#10234b',
      light: '#5f76ad',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#a33446',
    },
    error: {
      main: '#af2e3d', // your brand red
    },
    background: {
      default: '#f6f8fb',
      paper: '#fff',
    },
    text: {
      primary: '#111827',
      secondary: '#566274',
    },
    divider: '#e2e8f0',
  },
  typography: {
    fontFamily:
      'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    h1: { fontWeight: 800, letterSpacing: '-0.035em' },
    h2: { fontWeight: 800, letterSpacing: '-0.025em' },
    h3: { fontWeight: 750, letterSpacing: '-0.02em' },
    h4: { fontWeight: 750, letterSpacing: '-0.015em' },
    h5: { fontWeight: 750, letterSpacing: '-0.01em' },
    body1: { lineHeight: 1.7 },
    body2: { lineHeight: 1.65 },
  },
  shape: { borderRadius: 10 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 700,
          textTransform: 'none',
          boxShadow: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: { root: { backgroundImage: 'none' } },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: 8, backgroundColor: '#fff' },
      },
    },
  },
});

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      {/* resets browser default margins/padding */}
      <CssBaseline />

      <BrowserRouter>
        <SeoManager />
        <RouteScrollManager />
        <AppContent />
      </BrowserRouter>
    </ThemeProvider>
  );
}

function RouteScrollManager() {
  const { pathname, hash } = useLocation();

  useLayoutEffect(() => {
    if (typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent)) return undefined;

    if (!hash) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      return undefined;
    }

    const frame = window.requestAnimationFrame(() => {
      const target = document.getElementById(decodeURIComponent(hash.slice(1)));
      if (target) target.scrollIntoView({ block: 'start', behavior: 'auto' });
      else window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [pathname, hash]);

  return null;
}

function AppContent() {
  const location = useLocation();
  if (location.pathname.startsWith('/admin')) {
    return <Routes><Route path="/admin" element={<AdminPage />} /><Route path="*" element={<Navigate to="/admin" replace />} /></Routes>;
  }
  return (
    <>
      <Header />
      <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/buyers" element={<BuyersPage />} />
          <Route path="/landlords" element={<LandlordsPage />} />
          <Route path="/sellers" element={<SellersPage />} />
          <Route path="/tenants" element={<TenantsPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/repairs" element={<ReportRepairPage />} />
          <Route path="/payments" element={<PaymentsPage />} />
          <Route path="sellers/valuation" element={<ValuationPage />} />
          <Route path="/news/:slug" element={<NewsArticlePage />} />
          <Route path="/news" element={<Navigate to="/#news" replace />} />
          <Route path="/property/:slug" element={<PropertyPage />} />
          <Route path="/properties" element={<PropertiesAdvancedPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/privacy-policy" element={<PrivacyPage />} />
          <Route path="/cookies" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />

          {/* other routes */}
      </Routes>
      <CookieConsent />
      <Footer />
    </>
  );
}
